import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '@medflex/components/stories/Modal';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Logger from '@medflex/utils/Logger';
import InputField from '@medflex/components/stories/InputField';
import Button from '@medflex/components/stories/Button';
import { IconCloseSolid } from '@medflex/components/stories/Icons';
import Spinner from '@medflex/components/stories/Spinner';
import { showSuccessToast, showAlertToast } from '@medflex/components/stories/Toast';
import { trackEvent } from '@medflex/utils/Tracking';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import useUserProfile from '../hooks/useUserProfile';
import useModal from '../hooks/useModal';
import { MODALS } from '../hooks/useIsModal';

const validationSchema = yup.object().shape({
  services: yup.array(yup.string()),
});

const ServicesEditModal = () => {
  const [currentServices, setCurrentServices] = useState([]);
  const { close } = useModal();
  const dispatch = useDispatch();
  const { userProfile, editContactPage, isUpdating } = useUserProfile();
  const { handleSubmit: wrapSubmit, formState } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });
  const { t } = useTranslation('physician');
  const userProfileErrors = useSelector(e => e.errors.userProfile);
  const { services } = userProfile.attributes.pages;

  // Generate unique ID per service for creating unique keys in the
  // currentServices array, for correctly ordering/updating/deleting array values
  const generateUniqueId = () => Math.random().toString(16).slice(2);

  const servicesCount = services?.length;

  const defaultServices =
    servicesCount > 0
      ? services.map(service => ({
          id: generateUniqueId(),
          service,
        }))
      : [];

  // Create 4 empty inputs when no services are defined, otherwise create one empty field
  const initialValues = Array(servicesCount === 0 ? 4 : 1)
    .fill()
    .map(() => ({ id: generateUniqueId(), service: '' }));

  useEffect(() => {
    setCurrentServices([...defaultServices, ...initialValues]);
  }, [services]);

  const deleteService = index => {
    const updatedServices = [...currentServices];

    updatedServices.splice(index, 1);

    setCurrentServices(updatedServices);
  };

  const addService = () => {
    const updatedServices = [...currentServices];

    updatedServices.push({ id: generateUniqueId(), service: '' });

    setCurrentServices(updatedServices);
  };

  const updateService = (value, index) => {
    const updatedServices = [...currentServices];

    updatedServices[index] = {
      ...updatedServices[index],
      service: value,
    };

    setCurrentServices(updatedServices);
  };

  const showGenericError = () => {
    showAlertToast(t('ServicesEditModal.unexpected_error.title'));
  };

  const isSubmitDisabled = !formState.isValid || isUpdating;

  useEffect(() => {
    if (userProfileErrors) {
      Logger.error(`ServicesEditModal: ${JSON.stringify(userProfileErrors)}`);
      showGenericError();
      dispatch.errors.clear();
    }
  }, [userProfileErrors]);

  const handleSubmit = async event => {
    const payload = {
      services: currentServices
        .filter(({ service }) => service !== '')
        .map(({ service }) => service),
    };

    try {
      await editContactPage(payload);
      trackEvent(event, { actionText: 'edit' });
      close({ modal: MODALS.services });
      showSuccessToast(t('ServicesEditModal.success.title'));
    } catch (e) {
      e.message = `ServicesEditModal#handleSubmit(): ${e.message}`;
      Logger.error(e);
      showGenericError();
    }
  };

  const handleClose = e => {
    e.preventDefault();
    close({ modal: MODALS.services });
  };

  return (
    <Modal
      showCloseButton
      className="min-h-0 flex flex-col"
      onClickOutside={handleClose}
      onClickCloseButton={handleClose}
      title={t('ServicesEditModal.title')}
    >
      <div data-component="ServicesEditModal">
        <form onSubmit={wrapSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {currentServices.map(({ id, service }, index) => (
              <div key={id} className="space-y-2 col-span-1">
                <InputField
                  placeholder={t('ServicesEditModal.form.placeholder')}
                  value={service}
                  onChange={({ target }) => updateService(target.value, index)}
                  inputAction={
                    service !== '' && (
                      <button type="button" onClick={() => deleteService(index)}>
                        <IconCloseSolid size="4" className="text-slate-600" />
                      </button>
                    )
                  }
                />
              </div>
            ))}
          </div>
          <button
            className="flex items-center space-x-2 text-blue-500"
            type="button"
            onClick={addService}
          >
            <PlusCircleIcon className="w-7 h-7" />
            <span>{t('ServicesEditModal.form.add_field')}</span>
          </button>
          <div className="mt-6 flex w-full justify-end">
            <Button type="submit" isDisabled={isSubmitDisabled}>
              {isUpdating ? <Spinner /> : t('ServicesEditModal.form.save')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ServicesEditModal;
